import React, { useState } from 'react';
import { RefreshCw, CloudOff, Pencil, Archive, Trash2, Layers, X, Trash, Image as ImageIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { Button } from './ui/button';

const OP_ICON = {
  edit: Pencil,
  archive: Archive,
  delete: Trash2,
  'bulk-update': Layers,
  'bulk-delete': Layers,
  'add-photo': ImageIcon,
};

// Pull a displayable thumbnail out of a queued photo-add's request body.
// The offline queue stores the raw request `data` ({ image_base64 }); the
// base64 is stored without a data-URL prefix, so add one for <img>.
function photoThumb(entry) {
  if (!entry || entry.op !== 'add-photo') return null;
  let d = entry.data;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return null; } }
  const b64 = d && d.image_base64;
  if (!b64 || typeof b64 !== 'string') return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
}

// Compact "how long it's been waiting" — plain duration, no "ago" (the
// key already says "waiting {time}", so a bare duration reads cleanly).
function relTime(ts) {
  if (!ts) return '';
  const sec = Math.max(0, Math.round((Date.now() - Number(ts)) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

/**
 * Header pill that surfaces the offline edit queue: "N changes pending
 * sync". Tap to open a list of the pending changes with a "Retry now"
 * button. Renders nothing when the queue is empty.
 */
export const PendingSyncPill = ({ pending = [], pendingCount = 0, flushing = false, onFlush, onDiscard, onDiscardAll, isOnline = true, progress = { done: 0, total: 0 }, open: openProp, onOpenChange }) => {
  const { t } = useLanguage();
  const [openInternal, setOpenInternal] = useState(false);
  const [confirmDiscardAll, setConfirmDiscardAll] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openInternal;
  const setOpen = (v) => {
    if (controlled) { if (onOpenChange) onOpenChange(v); }
    else setOpenInternal(v);
  };

  if (pendingCount === 0 && !open) return null;

  const label = pendingCount === 1
    ? (t('pendingSyncPillOne') || '1 change pending sync')
    : (t('pendingSyncPill') || '{n} changes pending sync').replace('{n}', String(pendingCount));

  const describe = (entry) => {
    const name = (entry.names && entry.names[0]) || t('unnamedPlant') || 'Unnamed plant';
    const count = entry.count || 1;
    const plantWord = count === 1 ? (t('plant') || 'plant') : (t('plants') || 'plants');
    switch (entry.op) {
      case 'edit': return (t('pendingOpEdit') || 'Edit "{name}"').replace('{name}', name);
      case 'archive': return (t('pendingOpArchive') || 'Archive "{name}"').replace('{name}', name);
      case 'delete': return (t('pendingOpDelete') || 'Delete "{name}"').replace('{name}', name);
      case 'bulk-update': return (t('pendingOpBulkUpdate') || 'Update {count} {plantWord}').replace('{count}', String(count)).replace('{plantWord}', plantWord);
      case 'bulk-delete': return (t('pendingOpBulkDelete') || 'Delete {count} {plantWord}').replace('{count}', String(count)).replace('{plantWord}', plantWord);
      case 'add-photo': return (t('pendingOpAddPhoto') || 'Add photo to \u201c{name}\u201d').replace('{name}', name);
      default: return entry.op;
    }
  };

  return (
    <>
      {pendingCount > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
          style={{ backgroundColor: 'hsl(40, 90%, 90%)', color: 'hsl(35, 60%, 28%)' }}
          aria-label={label}
          data-testid="pending-sync-pill"
        >
          {flushing
            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            : <CloudOff className="w-3.5 h-3.5" aria-hidden="true" />}
          <span>{label}</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pending-sync-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>
              {t('pendingSyncTitle') || 'Changes waiting to sync'}
            </DialogTitle>
            <DialogDescription>
              {isOnline
                ? (t('pendingSyncBlurbOnline') || 'These changes are saved on this device and will sync automatically.')
                : (t('pendingSyncBlurbOffline') || 'You\u2019re offline. These will sync as soon as you reconnect.')}
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="pending-sync-list">
            {pending.map((entry) => {
              const Icon = OP_ICON[entry.op] || Pencil;
              const thumb = photoThumb(entry);
              return (
                <li
                  key={entry.key}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
                  style={{ backgroundColor: 'hsl(82, 55%, 97%)', border: '1px solid hsl(143, 28%, 90%)' }}
                  data-testid={`pending-sync-item-${entry.key}`}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      className="w-9 h-9 flex-shrink-0 rounded-md object-cover border"
                      style={{ borderColor: 'hsl(143, 28%, 85%)' }}
                      data-testid={`pending-sync-thumb-${entry.key}`}
                    />
                  ) : (
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(143, 28%, 40%)' }} aria-hidden="true" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
                      {describe(entry)}
                    </div>
                    {entry.created_at && (
                      <div className="text-xs" style={{ color: 'hsl(140, 25%, 32%)' }} data-testid={`pending-sync-age-${entry.key}`}>
                        {(t('pendingWaitingFor') || 'waiting {time}').replace('{time}', relTime(entry.created_at))}
                      </div>
                    )}
                  </div>
                  {onDiscard && (
                    <button
                      type="button"
                      onClick={() => onDiscard(entry)}
                      disabled={flushing}
                      className="flex-shrink-0 inline-flex items-center justify-center min-w-[28px] min-h-[28px] rounded-full transition-colors hover:bg-red-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50 disabled:opacity-50"
                      aria-label={(t('pendingSyncDiscard') || 'Discard this change')}
                      title={(t('pendingSyncDiscard') || 'Discard this change')}
                      data-testid={`pending-sync-discard-${entry.key}`}
                    >
                      <X className="w-3.5 h-3.5" style={{ color: 'hsl(0, 60%, 45%)' }} aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'hsl(143, 28%, 40%)' }} data-testid="pending-sync-progress">
                {flushing && progress.total > 0
                  ? (t('syncProgress') || '{done} of {total} synced').replace('{done}', String(progress.done)).replace('{total}', String(progress.total))
                  : ''}
              </span>
            </div>
            <div className="flex gap-2">
              {onDiscardAll && pendingCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDiscardAll(true)}
                  disabled={flushing}
                  className="gap-1.5"
                  style={{ color: 'hsl(0, 60%, 45%)' }}
                  data-testid="pending-sync-discard-all"
                >
                  <Trash className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('pendingSyncDiscardAll') || 'Discard all'}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="pending-sync-close">
                {t('done') || 'Got it'}
              </Button>
              <Button
                onClick={() => onFlush && onFlush()}
                disabled={flushing || !isOnline}
                className="gap-1.5"
                style={{ backgroundColor: 'hsl(143, 28%, 40%)', color: 'white' }}
                data-testid="pending-sync-retry"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${flushing ? 'animate-spin' : ''}`} aria-hidden="true" />
                {flushing ? (t('pendingSyncSyncing') || 'Syncing…') : (t('pendingSyncRetry') || 'Retry now')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscardAll} onOpenChange={setConfirmDiscardAll}>
        <AlertDialogContent data-testid="pending-sync-discard-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('pendingSyncDiscardAllConfirmTitle') || 'Discard all pending changes?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCount === 1
                ? (t('pendingSyncDiscardAllConfirmBodyOne') || 'This removes 1 change waiting to sync and reverts it on this device.')
                : (t('pendingSyncDiscardAllConfirmBody') || 'This removes {n} changes waiting to sync and reverts them on this device.').replace('{n}', String(pendingCount))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pending-sync-discard-all-cancel">
              {t('cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmDiscardAll(false); setOpen(false); if (onDiscardAll) onDiscardAll(); }}
              style={{ backgroundColor: 'hsl(0, 60%, 45%)', color: 'white' }}
              data-testid="pending-sync-discard-all-confirm"
            >
              {t('pendingSyncDiscardAll') || 'Discard all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PendingSyncPill;
